/**
 * POTransactionsStepper
 * 4-step dialog to create a Purchase Order Transaction (pending).
 *
 * Step 1 — Select PO (autofills vendor, PO#, date, items, quantities)
 * Step 2 — Configure Stock Details: Qty + Unit Cost only (no lot/batch/expiry)
 * Step 3 — Delivery Date & Location
 * Step 4 — Review & Submit → delivery_status = 'ordered' (NOT in inventory yet)
 */
import { useState, useEffect, useMemo } from 'react';
import {
  Dialog, Box, Typography, IconButton, Stepper, Step, StepLabel, StepContent,
  Button, CircularProgress, Chip, useTheme, alpha
} from '@mui/material';
import { Close as CloseIcon, LocalShipping as ShipIcon } from '@mui/icons-material';
import { supabase } from '../../../supabaseClient';
import { getInventoryConfig } from '../../inventory/inventory-components/shared/inventoryConfig';
import { createPendingPOTransaction } from './poTransactionHelpers';
import POSelectStep from './POSelectStep';
import POStockConfigStep from './POStockConfigStep';
import PODeliveryStep from './PODeliveryStep';

const STEPS = ['Select Purchase Order', 'Configure Stock Details', 'Delivery Details', 'Review & Submit'];

const TYPE_COLORS = {
  products: '#1565c0', chemicals: '#7b1fa2', caps: '#00796b',
  bottles: '#2e7d32', labels: '#e65100', supplies: '#5d4037',
};

function ReviewRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 0.5 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', minWidth: 90, flexShrink: 0 }}>{label}</Typography>
      <Typography variant="caption" sx={{ fontWeight: 500 }}>{value}</Typography>
    </Box>
  );
}

export default function POTransactionsStepper({ open, onClose, companyId, locations, onSuccess }) {
  const theme = useTheme();

  const [activeStep,    setActiveStep]    = useState(0);
  const [selectedPO,    setSelectedPO]    = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [itemsConfig,   setItemsConfig]   = useState({});
  const [shared,        setShared]        = useState({ vendor: null, poNumber: '', poDate: '', invoiceNumber: '', invoiceDate: '' });
  const [deliveryInfo,  setDeliveryInfo]  = useState({ expectedDate: '', locationId: '', notes: '' });
  const [submitting,    setSubmitting]    = useState(false);

  // Reset on close
  useEffect(() => {
    if (!open) {
      setActiveStep(0); setSelectedPO(null); setSelectedItems([]); setItemsConfig({});
      setShared({ vendor: null, poNumber: '', poDate: '', invoiceNumber: '', invoiceDate: '' });
      setDeliveryInfo({ expectedDate: '', locationId: '', notes: '' });
      setSubmitting(false);
    }
  }, [open]);

  // Autofill from selected PO
  const handleSelectPO = async (po) => {
    setSelectedPO(po);
    setShared({
      vendor:        po.suppliers ? { id: po.supplier_id, name: po.suppliers.name } : null,
      poNumber:      po.vendor_PO_number || '',
      poDate:        po.vendor_PO_date   || '',
      invoiceNumber: '',
      invoiceDate:   '',
    });
    setDeliveryInfo(prev => ({
      ...prev,
      expectedDate: po.expected_delivery_date || '',
    }));

    if (!po.vendor_PO_items?.length) return;

    const lineItems = po.vendor_PO_items;
    const typeGroups = {};
    lineItems.forEach(li => {
      if (!typeGroups[li.item_type]) typeGroups[li.item_type] = [];
      typeGroups[li.item_type].push(li.item_id);
    });

    const itemDetails = {};
    for (const [type, ids] of Object.entries(typeGroups)) {
      const config = getInventoryConfig(type);
      if (!config) continue;
      const { data } = await supabase.from(config.tableName).select('*').in('id', ids).eq('company_id', companyId);
      (data || []).forEach(d => { itemDetails[d.id] = { ...d, inventoryType: type }; });
    }

    const items = [];
    const cfg   = {};
    lineItems.forEach(li => {
      const detail = itemDetails[li.item_id];
      if (!detail) return;
      const item = { id: li.item_id, inventoryType: li.item_type, name: li.item_name || detail.name, sku: detail.sku || '', ...detail };
      items.push(item);
      const key = `${li.item_type}:${li.item_id}`;
      // All types use lots. Lot/batch captured at Confirm Delivery.
      cfg[key] = { lots: [{
        quantity: String(li.quantity  || ''),
        unitCost: String(li.unit_price || detail.unit_cost || ''),
      }]};
    });

    setSelectedItems(items);
    setItemsConfig(cfg);
  };

  // Validation
  // Step 0: PO selected + vendor name + PO number + PO date all required
  const canStep0 = !!(selectedPO && shared.vendor?.name && shared.poNumber && shared.poDate);

  // Step 1: every item needs at least one lot with qty > 0 (and unit config for products)
  const canStep1 = useMemo(() => {
    if (!selectedItems.length) return false;
    return selectedItems.every(item => {
      const key = `${item.inventoryType}:${item.id}`;
      const cfg = itemsConfig[key];
      if (item.inventoryType === 'products') {
        return cfg?.variantId &&
          cfg?.lots?.some(lot => lot._configId &&
            (parseFloat(lot.unitQty) > 0 || parseFloat(lot.caseQty) > 0 || parseFloat(lot.palletQty) > 0));
      }
      return cfg?.lots?.some(lot => parseFloat(lot.quantity) > 0);
    });
  }, [selectedItems, itemsConfig]);

  // Step 2: location required
  const canStep2 = !!deliveryInfo.locationId;

  // Submit
  const handleSubmit = async () => {
    setSubmitting(true);
    const location = locations.find(l => l.id === deliveryInfo.locationId);
    const errors = [];

    try {
      for (const item of selectedItems) {
        const key    = `${item.inventoryType}:${item.id}`;
        const cfg    = itemsConfig[key];
        const config = getInventoryConfig(item.inventoryType);

        const variantId = item.inventoryType === 'products' ? (cfg?.variantId || null) : null;
        for (const lot of (cfg?.lots || [])) {
          try {
            await createPendingPOTransaction(companyId, item.inventoryType, config, { item, location, lot, shared, deliveryInfo, variantId });
          } catch (e) { errors.push(`${item.name}: ${e.message}`); }
        }
      }

      if (errors.length === 0) {
        // Update the PO status to 'confirmed'
        if (selectedPO?.id) {
          const { error: poUpdateError } = await supabase
            .from('vendor_PO')
            .update({ status: 'confirmed' })
            .eq('id', selectedPO.id);
          if (poUpdateError) console.error('PO status update failed:', poUpdateError);
        }
        onSuccess?.('Purchase order transaction submitted — pending delivery confirmation');
        onClose();
      } else {
        onSuccess?.(`${selectedItems.length - errors.length} succeeded, ${errors.length} failed`, 'warning');
      }
    } catch (err) {
      onSuccess?.(`Error: ${err.message}`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const selectedLocation = locations?.find(l => l.id === deliveryInfo.locationId);

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      disableEscapeKeyDown maxWidth="xl" fullWidth
      PaperProps={{ sx: {
        borderRadius: 3,
        background: `linear-gradient(135deg, ${alpha(theme.palette.grey[50], 0.95)}, ${alpha(theme.palette.grey[100], 0.95)})`,
        boxShadow: `0 24px 48px ${alpha(theme.palette.primary.main, 0.15)}`,
        maxHeight: '92vh', overflow: 'hidden'
      }}}
    >
      {/* Header */}
      <Box sx={{
        p: 3, borderBottom: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)}, ${alpha(theme.palette.primary.main, 0.04)})`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box sx={{ p: 1.5, borderRadius: '50%', bgcolor: alpha(theme.palette.primary.main, 0.12), display: 'flex' }}>
            <ShipIcon sx={{ fontSize: 28, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 500 }}>Create Purchase Order Transaction</Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              {selectedPO ? `PO ${selectedPO.vendor_PO_number} · ${selectedPO.suppliers?.name || ''}` : 'Select a PO to begin'}
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selectedItems.length > 0 && (
            <Chip label={`${selectedItems.length} item${selectedItems.length !== 1 ? 's' : ''}`} size="small" color="primary" variant="outlined" />
          )}
          <IconButton onClick={onClose} sx={{ '&:hover': { transform: 'rotate(90deg)' }, transition: 'all 0.2s' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Stepper body */}
      <Box sx={{ p: 3, maxHeight: 'calc(92vh - 120px)', overflowY: 'auto' }}>
        <Stepper activeStep={activeStep} orientation="vertical">

          {/* Step 0 — Select PO */}
          <Step>
            <StepLabel><Typography variant="h6" sx={{ fontWeight: 500 }}>{STEPS[0]}</Typography></StepLabel>
            <StepContent>
              <POSelectStep companyId={companyId} selectedPO={selectedPO} onSelect={handleSelectPO} />
              <Box sx={{ mt: 3 }}>
                <Button variant="contained" onClick={() => setActiveStep(1)} disabled={!canStep0}
                  sx={{ borderRadius: 2, px: 5, py: 1.5, fontWeight: 500, fontSize: '1rem' }}>
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 1 — Qty + Unit Cost */}
          <Step>
            <StepLabel><Typography variant="h6" sx={{ fontWeight: 500 }}>{STEPS[1]}</Typography></StepLabel>
            <StepContent>
              {selectedItems.length > 0 ? (
                <POStockConfigStep
                  companyId={companyId}
                  selectedItems={selectedItems}
                  itemsConfig={itemsConfig}
                  onItemsConfigChange={setItemsConfig}
                  shared={shared}
                  onSharedChange={setShared}
                />
              ) : (
                <Typography color="text.secondary">Select a PO in Step 1 first.</Typography>
              )}
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button onClick={() => setActiveStep(0)} sx={{ borderRadius: 2, px: 4, py: 1.5 }}>Back</Button>
                <Button variant="contained" onClick={() => setActiveStep(2)} disabled={!canStep1}
                  sx={{ borderRadius: 2, px: 5, py: 1.5, fontWeight: 500 }}>
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 2 — Delivery Details */}
          <Step>
            <StepLabel><Typography variant="h6" sx={{ fontWeight: 500 }}>{STEPS[2]}</Typography></StepLabel>
            <StepContent>
              <PODeliveryStep deliveryInfo={deliveryInfo} onChange={setDeliveryInfo} locations={locations || []} />
              <Box sx={{ mt: 3, display: 'flex', gap: 2 }}>
                <Button onClick={() => setActiveStep(1)} sx={{ borderRadius: 2, px: 4, py: 1.5 }}>Back</Button>
                <Button variant="contained" onClick={() => setActiveStep(3)} disabled={!canStep2}
                  sx={{ borderRadius: 2, px: 5, py: 1.5, fontWeight: 500 }}>
                  Continue
                </Button>
              </Box>
            </StepContent>
          </Step>

          {/* Step 3 — Review & Submit */}
          <Step>
            <StepLabel><Typography variant="h6" sx={{ fontWeight: 500 }}>{STEPS[3]}</Typography></StepLabel>
            <StepContent>
              {/* Summary banner */}
              <Box sx={{ mb: 2.5, p: 2, borderRadius: 2, bgcolor: alpha(theme.palette.primary.main, 0.06), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  PO {shared.poNumber} · {shared.vendor?.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {selectedItems.length} item{selectedItems.length !== 1 ? 's' : ''} · Expected delivery {deliveryInfo.expectedDate || '—'} at {selectedLocation?.name || '—'}
                </Typography>
              </Box>

              {/* Item review cards */}
              {selectedItems.map(item => {
                const key   = `${item.inventoryType}:${item.id}`;
                const cfg   = itemsConfig[key] || {};
                const color = TYPE_COLORS[item.inventoryType] || '#666';
                const lots  = cfg.lots || [];
                return (
                  <Box key={key} sx={{ mb: 2, borderRadius: 2, border: `1px solid ${alpha(color, 0.25)}`, overflow: 'hidden' }}>
                    <Box sx={{ px: 2.5, py: 1.5, bgcolor: alpha(color, 0.07), borderBottom: `1px solid ${alpha(color, 0.2)}`, display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Chip label={item.inventoryType.charAt(0).toUpperCase() + item.inventoryType.slice(1)} size="small" sx={{ bgcolor: alpha(color, 0.15), color, fontWeight: 500, height: 20, fontSize: '0.7rem' }} />
                      <Typography variant="subtitle2" sx={{ fontWeight: 500, color }}>{item.name}</Typography>
                    </Box>
                    {lots.map((lot, li) => (
                      <Box key={li} sx={{ px: 2.5, py: 1.5, bgcolor: li % 2 === 0 ? 'white' : alpha(theme.palette.grey[50], 0.6) }}>
                        <ReviewRow label="Qty" value={(parseFloat(lot.quantity) || 0).toLocaleString()} />
                        {lot.unitCost && <ReviewRow label="Unit Cost" value={`$${parseFloat(lot.unitCost).toFixed(2)}`} />}
                      </Box>
                    ))}
                  </Box>
                );
              })}

              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button onClick={() => setActiveStep(2)} sx={{ borderRadius: 2, px: 4, py: 1.5 }}>Back</Button>
                <Button
                  variant="contained" onClick={handleSubmit} disabled={submitting}
                  startIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <ShipIcon />}
                  sx={{ borderRadius: 2, px: 5, py: 1.5, fontWeight: 500, fontSize: '1rem', bgcolor: 'primary.main', '&:hover': { bgcolor: 'primary.dark' } }}
                >
                  {submitting ? 'Submitting...' : 'Submit Purchase Order'}
                </Button>
              </Box>
            </StepContent>
          </Step>
        </Stepper>
      </Box>
    </Dialog>
  );
}
